import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { PromotionNotAppliedReasonType, PromotionNotAppliedWarning, YumCart, useApplyPromoCodeMutation } from '@pizza-hut-us-development/client-core';

import { useDecision } from '@optimizely/react-sdk';
import { useCartRail } from '@noqo/clients/web/providers/CartRailProvider';
import { hideLoadingBar, openLocalizationRail, openModal, showLoadingBar } from '@/localization/actions';

import Routes from '@/router/routes';
import tel from '@/telemetry';

import { errorSelectors } from '@/graphql/errors/selectors';
import { orderSelectors } from '@/clientCore/redux/selectors/orderSelectors';
import { localizationSelectors } from '@/localization/localizationSelectors';
import { userDomainSelectors } from '@/header/userDomainSelectors';
import { cartSelectors } from '@/cart/cartSelectors';

import {
  selectDealToDisplay,
  setDealToDisplay
} from '@/builders/deals/slice/dealToDisplay.slice';
import { createItemAnalyticsForNationalDeals, onNationalDealMenuView } from '@/builders/deals/analytics/analytics';
import { useRedirectErrorHandling } from '@/hooks/useErrorHandling';
import { useApplicationInitialized } from '@/hooks/useApplicationInitialized';
import {
  useClearDealOnRouteChange,
  useClearDealOnStoreChange,
  useHistoryOnBrowserBack
} from '@/builders/deals/hooks/dealsPage';

import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import { isRedemptionWarning, isResolvableWarning } from '@/clientCore/cart/components/CartRail/components/CartContent/components/YumAddedCoupons/helpers';
import { openCartRail } from '@/clientCore/redux/rail/CartRailSlice';
import useFeatureFlagOverride from '@/hooks/useFeatureFlagOverride';

import dealsData from '@/common/pageSetup/nationalData/dealsData';
import { DealsProps } from '../../../pages/deals';
import { RedemptionQueryParams } from '@/clientCore/redemptionMenu/constants';
import { nationalData } from '@/common/pageSetup/nationalData/nationalDataHelpers';
import { getErrMessage, getStatusCode } from '@/telemetry/helpers';
import { dealNotAvailableModalDetails } from '@/common/Modal/modalDetails';

export const useDealsPage = ({
  dealGlobalId, ssrPrivateCode, ssrError, hasValidPrivateCode, id
}: DealsProps) => {
  const dealError = useSelector(errorSelectors.dealGqlError);
  const pizzaError = useSelector(errorSelectors.pizzaGqlError);
  const userIsLocalized = useSelector(localizationSelectors.isLocalized);
  const dispatch = useDispatch();
  const dealId = useSelector(selectDealToDisplay)?.id;
  const router = useRouter();
  const analytics = useAnalytics();
  const isAppInitialized = useApplicationInitialized();
  const isDealBuilder = !!(dealGlobalId || dealId);
  const nationalDeals = dealsData();
  const isGuest = useSelector(userDomainSelectors.isGuest);
  const cartQuantity = useSelector(cartSelectors.quantity);
  const cart = useSelector(orderSelectors.cart);
  const storeDetails = useSelector(localizationSelectors.storeDetails);

  const [{ enabled: dealRoutingImprovementsEnabled }] = useDecision('fr-ims-679-deal-routing-improvements');

  const [applyYumPromoCode] = useApplyPromoCodeMutation();
  const updatedNationalDeals = nationalData(nationalDeals);
  const { enabled: isNewNOQOCartEnabled } = useFeatureFlagOverride('noqo-295-web-cart-rail-v2');
  const { openCartRail: openCartRailV2 } = useCartRail();

  const generateModalDetails = (code: string, responseCart: YumCart) => {
    const modalDetails: ModalContent = {
      title: 'Coupon Applied!',
      body: 'Your coupon has been added to the cart.',
      cta: {
        text: 'Ok'
      },
      onClose: () => {
        if (isNewNOQOCartEnabled) {
          openCartRailV2();
        } else {
          dispatch(openCartRail());
        }
      }
    };

    const associatedWarning = responseCart.warnings?.find((warning) => {
      if (isRedemptionWarning(warning)) return 'code' in warning && warning.code === code;
      return false;
    }) as PromotionNotAppliedWarning | undefined;

    if (!associatedWarning) return modalDetails;

    const warningReasonTypes = associatedWarning.reasons.map((reason) => reason.__typename);

    if (warningReasonTypes.includes(PromotionNotAppliedReasonType.SubtotalNotDiscountable)) {
      modalDetails.body = 'Your coupon has been added to the cart. Add an item to the cart to see your discount in checkout.';
      modalDetails.onClose = () => router.push(Routes.MENU.PIZZA);
    };

    if (warningReasonTypes.includes(PromotionNotAppliedReasonType.ItemPresenceRequirementNotMet)) {
      modalDetails.onClose = () => router.push(`/redeem?red=${associatedWarning.promotionId}&fl=${RedemptionQueryParams.REQUIREMENTS}`);
    };

    if (warningReasonTypes.includes(PromotionNotAppliedReasonType.PromotionEffectTargetNotInCart)) {
      modalDetails.onClose = () => router.push(`/redeem?red=${associatedWarning.promotionId}&fl=${RedemptionQueryParams.EFFECTS}`);
    };

    return modalDetails;
  };

  // NEW - FF'd flow
  const attemptedToAddPromo = useRef(false);
  useEffect(() => {
    if (!dealRoutingImprovementsEnabled) return;
    if (attemptedToAddPromo.current || !cart || !cart.storeNumber?.length) return;
    if (storeDetails && dealGlobalId) {
      if (dealRoutingImprovementsEnabled) dispatch(showLoadingBar());
      attemptedToAddPromo.current = true;
      applyYumPromoCode(dealGlobalId).then((res) => {
        if ('data' in res) {
          const responseCart = res.data as YumCart;

          const isSuccessfullyApplied = responseCart.appliedPromotions?.some((promo) => promo.code === dealGlobalId);

          const isSuccessfulWarning = responseCart.warnings?.some((warning) => {
            if ('code' in warning && warning.code === dealGlobalId) {
              if (isRedemptionWarning(warning)) return isResolvableWarning(warning);
            }
            return false;
          });

          // If redemption code isn't valid, attempt to treat as deal
          if (!isSuccessfullyApplied && !isSuccessfulWarning) {
            if (dealRoutingImprovementsEnabled) dispatch(hideLoadingBar());
            dispatch(setDealToDisplay({ dealId: ssrPrivateCode || dealGlobalId, publicCode: dealGlobalId }));
            return;
          }
          tel.addCustomEvent(tel.CUSTOM_EVENT_NAME.CART.PROMO.ADD, 'success', {
            id: dealGlobalId
          });
          if (dealRoutingImprovementsEnabled) dispatch(hideLoadingBar());
          router.push(Routes.DEALS); // Push to deals, to clear the globalId
          dispatch(openModal(generateModalDetails(dealGlobalId, responseCart)));
        } else if ('error' in res) {
          if (dealRoutingImprovementsEnabled) dispatch(hideLoadingBar());
          tel.addCustomEvent(tel.CUSTOM_EVENT_NAME.CART.PROMO.ADD, 'failure', {
            id: dealGlobalId,
            http_code: getStatusCode(res.error),
            err_msg: getErrMessage(res.error)
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.storeNumber, ssrPrivateCode, isGuest, cartQuantity, storeDetails?.storeNumber]);

  // LEGACY - Remove after FF retired
  const legacyAttemptedToAddPromo = useRef(false);
  const legacyCompletedPromoAdd = useRef(false);
  useEffect(() => {
    if (dealRoutingImprovementsEnabled) return;
    if (storeDetails && dealGlobalId) {
      if (!legacyAttemptedToAddPromo.current) {
        // Try to add as promo to cart, if not, set as deal for deal builder
        if (!cart || !cart.storeNumber?.length) return;
        legacyAttemptedToAddPromo.current = true;
        applyYumPromoCode(dealGlobalId).then((res) => {
          legacyCompletedPromoAdd.current = true;
          if ('data' in res) {
            const responseCart = res.data as YumCart;

            const isSuccessfullyApplied = responseCart.appliedPromotions?.some((promo) => promo.code === dealGlobalId);

            const isSuccessfulWarning = responseCart.warnings?.some((warning) => {
              if ('code' in warning && warning.code === dealGlobalId) {
                if (isRedemptionWarning(warning)) return isResolvableWarning(warning);
              }
              return false;
            });

            // If redemption code isn't valid, attempt to treat as deal
            if (!isSuccessfullyApplied && !isSuccessfulWarning) {
              dispatch(setDealToDisplay({ dealId: dealGlobalId, publicCode: dealGlobalId }));
              return;
            }
            tel.addCustomEvent(tel.CUSTOM_EVENT_NAME.CART.PROMO.ADD, 'success', {
              id: dealGlobalId
            });
            router.push(Routes.DEALS); // Push to deals, to clear the globalId
            dispatch(openModal(generateModalDetails(dealGlobalId, responseCart)));
          } else if ('error' in res) {
            tel.addCustomEvent(tel.CUSTOM_EVENT_NAME.CART.PROMO.ADD, 'failure', {
              id: dealGlobalId,
              http_code: getStatusCode(res.error),
              err_msg: getErrMessage(res.error)
            });
          }
        });
      } else if (hasValidPrivateCode && ssrPrivateCode && legacyCompletedPromoAdd.current) {
        dispatch(setDealToDisplay({ dealId: ssrPrivateCode, publicCode: dealGlobalId }));
      } else if (!hasValidPrivateCode && !ssrError && legacyCompletedPromoAdd.current) {
        router.push(Routes.DEALS);
        dispatch(openModal(dealNotAvailableModalDetails));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.storeNumber, ssrPrivateCode, isGuest, cartQuantity, storeDetails?.storeNumber]);

  useClearDealOnStoreChange();
  useClearDealOnRouteChange();
  useHistoryOnBrowserBack();

  useEffect(() => {
    if (dealGlobalId && !userIsLocalized) {
      router.replace(Routes.DEALS, undefined, { shallow: true });
      if (id) {
        dispatch(openLocalizationRail({ routeTo: `${Routes.DEALS}?id=${id}` }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealGlobalId, userIsLocalized]);

  useEffect(() => {
    if (!userIsLocalized && isAppInitialized) {
      const items = createItemAnalyticsForNationalDeals(nationalDeals);
      analytics.push(() => onNationalDealMenuView(items));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsLocalized, isAppInitialized]);

  useRedirectErrorHandling(ssrError!, (isDealBuilder) ? Routes.DEALS : Routes.MENU.MENU);

  const isError = dealError || pizzaError;
  const isDealBuilderPage = isDealBuilder && userIsLocalized &&  !!dealId;

  return {
    isError,
    isDealBuilderPage,
    dealId,
    userIsLocalized,
    updatedNationalDeals
  };
};
